import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import { Card } from '@/components/ui/card';
import { usePublicSeo } from '@/utils/seo';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'Général',
      questions: [
        {
          question: "Qu'est-ce que Coli Noki ?",
          answer:
            'Coli Noki est une plateforme de gestion de livraisons qui connecte vendeurs, livreurs, points relais et clients pour le suivi complet des colis.',
        },
        {
          question: 'Comment fonctionne la livraison ?',
          answer:
            'Le vendeur crée la livraison, paie la commande avec le portefeuille ou un paiement direct, puis la mission passe en traitement jusqu’à la remise au client avec suivi des statuts.',
        },
        {
          question: 'Comment fonctionne le suivi de colis ?',
          answer:
            'Le client saisit le code de suivi sur la page publique Suivi de colis pour consulter le statut, les informations du colis et les événements de livraison.',
        },
      ],
    },
    {
      category: 'Plans et options',
      questions: [
        {
          question: 'Quelle est la différence entre Standard et Express ?',
          answer:
            'Standard est le service de base. Express est une option de livraison prioritaire réservée aux vendeurs avec Coli Noki Pro actif.',
        },
        {
          question: 'Comment fonctionne Coli Noki Pro ?',
          answer:
            'Coli Noki Pro est le plan premium. Il active notamment le service express, l’assurance colis, les intégrations et des options avancées.',
        },
        {
          question: 'Comment fonctionne l’assurance colis ?',
          answer:
            'L’assurance peut être activée lors de la création de livraison. Le backend calcule le supplément selon la valeur déclarée du colis, en général entre 10 % et 15 %.',
        },
      ],
    },
    {
      category: 'Comptes et rôles',
      questions: [
        {
          question: 'Comment devenir vendeur ?',
          answer:
            "Inscrivez-vous depuis la page d’inscription pour ouvrir un compte vendeur. Vous pourrez ensuite gérer vos livraisons, paiements et abonnements depuis votre tableau de bord.",
        },
        {
          question: 'Comment devenir livreur ?',
          answer:
            'Le compte livreur est activé après validation par l’administration. Le livreur reçoit ses missions dans son espace dédié.',
        },
        {
          question: 'Comment sont vérifiés les vendeurs (KYC) ?',
          answer:
            'Le vendeur soumet ses documents KYC. Après revue admin, le statut est approuvé ou rejeté. Le badge vendeur vérifié dépend de cette validation.',
        },
      ],
    },
    {
      category: 'Paiements, portefeuille et affiliation',
      questions: [
        {
          question: 'Comment fonctionne le portefeuille vendeur ?',
          answer:
            'Le portefeuille permet de consulter le solde, l’historique des transactions, de recharger le compte et de payer certaines livraisons selon les règles actives.',
        },
        {
          question: 'Comment fonctionne l’affiliation ?',
          answer:
            'Un affilié partage son lien d’affiliation. Les clics, inscriptions et commissions sont suivis, puis les paiements sont traités selon les règles admin.',
        },
        {
          question: 'Quels paiements sont acceptés ?',
          answer:
            'La plateforme supporte la carte bancaire ainsi que des flux Mobile Money / Airtel Money selon la configuration active.',
        },
      ],
    },
  ];

  usePublicSeo({
    title: 'FAQ Coli Noki | Aide livraison, portefeuille, affiliation',
    description:
      'Retrouvez les réponses sur les livraisons, Coli Noki Pro, l’assurance, le portefeuille, le suivi de colis, le KYC et l’affiliation.',
    path: '/faq',
    structuredData: [
      {
        id: 'faq-page',
        data: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.flatMap((category) => (
            category.questions.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            }))
          )),
        },
      },
    ],
  });

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-1">
        <section className="bg-gradient-to-br from-red-50 to-cyan-50 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <HelpCircle className="w-16 h-16 text-red-600 mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-6">Foire aux questions</h1>
              <p className="text-lg text-gray-700">Réponses claires sur les services publics et vendeurs de Coli Noki.</p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {faqs.map((category, categoryIndex) => (
                <div key={categoryIndex} className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{category.category}</h2>
                  <div className="space-y-4">
                    {category.questions.map((faq, questionIndex) => {
                      const isOpen = openIndex === `${categoryIndex}-${questionIndex}`;
                      return (
                        <Card key={questionIndex} className="overflow-hidden">
                          <button
                            onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                          >
                            <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                            {isOpen ? (
                              <ChevronUp className="w-5 h-5 text-red-600 flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                            )}
                          </button>
                          {isOpen && (
                            <div className="px-6 pb-4 text-gray-700 leading-relaxed border-t">
                              <p className="pt-4">{faq.answer}</p>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-br from-red-50 to-cyan-50 py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Besoin d’aide supplémentaire ?</h2>
              <p className="text-gray-700 mb-6">Notre équipe support peut vous accompagner sur vos parcours vendeur, livreur ou client.</p>
              <a
                href="/contact"
                className="inline-block bg-gradient-to-r from-red-500 to-cyan-500 hover:from-red-600 hover:to-cyan-600 text-white font-semibold px-8 py-3 rounded-lg transition-all"
              >
                Contacter le support
              </a>
              <p className="text-sm text-gray-600 mt-4">
                Pages utiles : <a href="/pricing" className="text-cyan-700 hover:underline">Tarifs</a>,{' '}
                <a href="/track" className="text-cyan-700 hover:underline">Suivi de colis</a>,{' '}
                <a href="/register" className="text-cyan-700 hover:underline">Inscription vendeur</a>.
              </p>
            </div>
          </div>
        </section>
      </main>

      <FooterNew />
    </div>
  );
};

export default FAQPage;
